import { GetStaticPaths, GetStaticProps, NextPage } from 'next';
import { useRouter } from 'next/router';
import { Collection, FAQPage, Thing, WithContext } from 'schema-dts';

import { getPageSlugs, hygraphClient } from '@/lib/graphql-clients';
import {
  Author,
  getSdk,
  Page,
  PageSchemaType,
  PageWhereInput,
  Section,
} from '@/lib/types';
import usePageLogging from '@/hooks/use-page-logging';

import AuthorShortBio from '@/components/author-short-bio';
import DefaultLayout from '@/components/layout/default-layout';

import { GlobalContextProvider, PageMetaProps } from '@/context/global-context';
import HeroRenderer from '@/hygraph/renderers/hero.renderer';
import SectionsRenderer from '@/hygraph/renderers/sections.renderer';
import seoTransformer from '@/hygraph/transformers/seo.transformer';
import { HomeRouterHandleType } from '@/pages/[...slug]';
import { RouteHandlerProps } from '@/router/types.router';
import PageStaticDataService, {
  PageStaticDataProps,
} from '@/services/page-static-data.service';
import SchemaService, { nomadHealthOrg } from '@/services/schema-service';
import { buildCanonicalLink } from '@/utils/canonical-link-utils';
import { getStaticPathsFromPageSlugs } from '@/utils/hygraph-utils';

type PageProps = {
  page: Page;
  pageStaticData: PageStaticDataProps;
  preview: boolean;
  schema: WithContext<Thing>[];
  canonicalLink: string | null;
};

const Component: NextPage<PageProps> = ({
  page,
  schema,
  pageStaticData,
  preview,
  canonicalLink,
}) => {
  usePageLogging(HomeRouterHandleType.Page);

  const router = useRouter();

  if (router.isFallback) {
    return <div>Loading...</div>;
  }

  const pageMeta: PageMetaProps = {
    ...seoTransformer(page.seo, { title: page.title }),
    navigationLabel: page.navigationLabel || undefined,
    templateSetting: page.templateSetting || undefined,
    canonicalLink,
  };

  return (
    <GlobalContextProvider
      schema={schema}
      pageStaticData={pageStaticData}
      pageMeta={pageMeta}
    >
      <DefaultLayout preview={preview}>
        <main className='flex grow flex-col'>
          <HeroRenderer hero={page?.hero} />
          <SectionsRenderer sections={page.sections} />

          {page.templateSetting?.showAuthor && page.author && (
            <AuthorShortBio {...(page.author as Author)} />
          )}
        </main>
      </DefaultLayout>
    </GlobalContextProvider>
  );
};

function buildPageSchema(page: Page): WithContext<Thing>[] {
  if (page.seo?.noIndex) return []; // Don't bother building schema if page is no-index

  const isCollection = page.seo?.schemaType === PageSchemaType.Collection;
  const isFaqPage = page.seo?.schemaType === PageSchemaType.FaqPage;

  const components = [page.hero?.section, ...page.sections]
    .filter((section): section is Section => !!section)
    .flatMap((section) => section.rows);

  const { images, questions, things } =
    SchemaService.buildSchemaData(components);

  const pageSchema = SchemaService.buildPageThing(page, images);
  pageSchema.mainEntity = isFaqPage ? questions : [];

  let collectionParts: Collection['hasPart'] = [pageSchema];

  if (!isFaqPage && questions.length > 0) {
    const faqPageThing: FAQPage = { '@type': 'FAQPage', mainEntity: questions };
    if (isCollection) {
      collectionParts = collectionParts.concat([faqPageThing]);
    } else {
      things.unshift({ '@context': 'https://schema.org', ...faqPageThing });
    }
  }

  if (!isCollection) {
    return [{ '@context': 'https://schema.org', ...pageSchema }, ...things];
  }

  const collectionThing: WithContext<Collection> = {
    '@context': 'https://schema.org',
    '@type': 'Collection',
    publisher: nomadHealthOrg,
    about: nomadHealthOrg,
    author: nomadHealthOrg,
    creator: nomadHealthOrg,
    hasPart: collectionParts,
  };
  return [collectionThing, ...things];
}

export const getStaticPaths: GetStaticPaths = async () => {
  const { pages } = await getPageSlugs();
  const paths = getStaticPathsFromPageSlugs(pages);
  return { paths, fallback: 'blocking' };
};

export const getStaticProps: GetStaticProps = async ({
  params,
  preview = false,
}) => {
  const sdk = getSdk(hygraphClient(preview));

  let filters: PageWhereInput | null = null;
  if (Array.isArray(params?.slug)) {
    params?.slug.forEach((slug) => {
      filters = {
        slug,
        parent: filters,
      };
    });
  } else {
    filters = { slug: params?.slug };
  }

  const { pages } = await sdk.Pages({
    filters,
  });
  const page = pages.pop() as Page | undefined;

  if (!page || !page.id) {
    return {
      notFound: true,
    };
  }

  const pageStaticData = await PageStaticDataService.get(page);
  const schema = buildPageSchema(page);
  const canonicalLink = buildCanonicalLink(params?.slug);

  return {
    props: {
      page,
      schema,
      pageStaticData,
      preview,
      canonicalLink,
    },
    revalidate: 3600,
  };
};

const PageHandler: RouteHandlerProps<PageProps> = {
  getStaticPaths,
  getStaticProps,
  Component,
};

export default PageHandler;
